import {
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  TextField,
} from '@mui/material';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { EmptySelect } from '@/components/selectors/emptySelect';
import { RdsInstanceClassSelect } from '@/components/selectors/rdsInstanceClassSelect';
import { RDSSubnetGroupSelect } from '@/components/selectors/rdsSubnetGroupSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { ContinuousSnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreRDSPitrState } from '../restoreRDSPitrWizard';

export const RDSPitrConfiguration = (props: StepProps<RestoreRDSPitrState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);

  const { data: inventoryResourceBody } = dal.inventory.useResource(
    props.wizardState.resourceId
  );

  const canGoNext = () => {
    return !(
      isRestoring ||
      !props.wizardState.regionName ||
      !props.wizardState.resourceName ||
      !props.wizardState.securityGroupIds ||
      !props.wizardState.rdsSubnetGroupName ||
      !props.wizardState.rdsInstanceClass ||
      !getEncryptionKey(props.wizardState.encryption)
    );
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };

    return dal.restore
      .rdsNativePitr(props.wizardState.resourceId, {
        restoreToTime: props.wizardState.restoreToTime,
        targetDatabaseName: props.wizardState.resourceName || '',
        primaryInstanceClass: props.wizardState.rdsInstanceClass || '',
        network: {
          subnetGroupName: props.wizardState.rdsSubnetGroupName || '',
          securityGroupIds: props.wizardState.securityGroupIds || [],
        },
        advancedOptions: {
          isRestoreToLatest: !props.wizardState.restoreToTime,
          tags,
        },
        ...(props.wizardState.isAuroraRds
          ? {
              clusterAdvancedOptions: {
                encryptionKeyId:
                  getEncryptionKey(props.wizardState.encryption) || '',
              },
            }
          : {}),
      })
      .then(() =>
        props.finish(
          `Started restore job for ${props.wizardState.resourceName}`
        )
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore RDS: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      hideStepper
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <Stack gap='24px'>
          <ContinuousSnapshotToolbar
            pointInTime={props.wizardState.restoreToTime}
            account={props.wizardState.sourceAccount}
            region={props.wizardState.regionName}
          />
          <Divider />
        </Stack>
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <FormControl size='small' className='flex-1'>
            <FormLabel>Database name</FormLabel>
            <TextField
              data-testid='databaseNameTextField'
              size='small'
              value={props.wizardState.resourceName || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  resourceName: event.target.value,
                }))
              }
            />
          </FormControl>
          <EmptySelect title='Region' value={props.wizardState.regionName} />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <RDSSubnetGroupSelect
            accountId={props.wizardState.sourceAccount?.id}
            regionName={props.wizardState.regionName}
            rdsSubnetGroupName={props.wizardState.rdsSubnetGroupName}
            onChange={(rdsSubnetGroupName, vpc) =>
              props.setWizardState((state) => ({
                ...state,
                rdsSubnetGroupName,
                vpc,
                securityGroupIds: undefined,
              }))
            }
          />
          <SecurityGroupSelect
            accountId={props.wizardState.sourceAccount?.id}
            regionName={props.wizardState.regionName}
            securityGroupIds={props.wizardState.securityGroupIds}
            vpc={props.wizardState.vpc || ''}
            isSubnetIdMandatory={false}
            isOptionalField={false}
            onChange={(securityGroupIds) =>
              props.setWizardState((state) => ({
                ...state,
                securityGroupIds,
              }))
            }
          />
        </Stack>
        <Stack
          direction='row'
          alignItems='center'
          gap='36px'
          className='mt-[20px]'
        >
          <RdsInstanceClassSelect
            supportInherit={false}
            accountId={props.wizardState.sourceAccount?.id}
            regionName={props.wizardState.regionName}
            engine={
              inventoryResourceBody?.resource.resourceProperties?.awsRds?.engine
            }
            engineVersion={
              inventoryResourceBody?.resource.resourceProperties?.awsRds
                ?.engineVersion
            }
            rdsInstanceClass={props.wizardState.rdsInstanceClass}
            isCluster={
              !!inventoryResourceBody?.resource?.resourceProperties?.awsRds
                ?.clusterId
            }
            onChange={(rdsInstanceClass) =>
              props.setWizardState((state) => ({
                ...state,
                rdsInstanceClass,
              }))
            }
          />
          <div className='flex-1' />
        </Stack>
      </Box>
      <Divider />
      {props.wizardState.isAuroraRds && (
        <CardContent className='p-0'>
          <EncryptionComponent
            encryption={props.wizardState.encryption}
            accountId={props.wizardState.sourceAccount?.id}
            regionName={props.wizardState.regionName}
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                encryption,
              }))
            }
          />
        </CardContent>
      )}
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
